<template>
  <div class="home">
    <div class="xl:block">
      <div class="container mx-auto">
        <template v-if="showSymptomChecker">
          <div class="cta py-5 text-center flex items-center">
            <h2 class="text-blue pr-5">See how a physio can help you</h2>
            <p class="text-blue pr-5">Use our new symptom checker for personalised advice</p>
            <a href="https://checker.ascenti.co.uk/" class="btn bg-blue text-white">Start now</a>
          </div>
        </template>
      </div>
    </div>
    <search-form />
    <welcome v-if="appointments.length === 0"/>

    <appointments-grid v-else />

    <div v-if="appointments.length > 0 && filteredAppointments.length > 5" class="mt-4 text-center">
      <button class="btn btn-primary inline" @click.prevent="toggleShowAllClinics">
        <span v-if="showAllClinics">Fewer clinics</span>
        <span v-else>More clinics</span>
      </button>
    </div>

    <cookie-law theme="ascenti" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

import CookieLaw from 'vue-cookie-law';

import AppointmentsGrid from '../components/Appointments/AppointmentsGrid';
import Welcome from '../components/Block/Welcome';
import SearchForm from '../components/Form/SearchForm';
import {getEarliestAppointment} from '../store/appointments';
import { Prices } from '../lib/Prices';

export default {
  name: 'home',

  components: {
    AppointmentsGrid,
    CookieLaw,
    Welcome,
    SearchForm
  },

  computed: {
    ...mapState('appointments', [
      'showAllClinics',
      'appointments',
    ]),

    showSymptomChecker() {
      return process.env.VUE_APP_NAV_SHOW_SYMPTOM_CHECKER === 'true'
    },

    filteredAppointments() {
      return this.$store.getters['appointments/filteredAppointments'];
    },

    priceLowest() {
      return Prices.outsideM25();
    },

    priceHighest() {
      return Prices.hasExceptionPrice() ? Prices.exception() : Prices.insideM25();
    }
  },

  mounted() {
    this.$gtm.trackView('Homepage', '/');

    this.showNavigation();

    this.setTheme('light');

    this.showComponent({ name: 'appointments-header', props: { ...this.headerProps() } });

    if (this.appointments.length) {
      this.showFooterAppointmentsMessage();
    } else {
      this.setFooterMessage(null);
    }

    this.listen('appointments.load', [
      this.showFooterAppointmentsMessage,
      this.appointmentsTracking
    ]);

    this.$scrollTo('#app');
  },

  destroyed() {
    this.silence('appointments.load', [
      this.showFooterAppointmentsMessage,
      this.appointmentsTracking
    ]);
  },

  methods: {
    ...mapActions('header', ['showComponent', 'showNavigation', 'setTheme']),

    ...mapActions('footer', ['setFooterMessage']),

    ...mapActions('appointments', ['toggleShowAllClinics']),

    headerProps() {
      return {
        pageTitle: 'Visit a clinic',
        switchBlockLabel: "Book video call",
        switchBlockLink: "/video",
        switchBlockTitle: "Rather a video call?",
        switchBlockContent: "Book an appointment for an online consultation.",
        copy: 'Personal consultation with one of our expert physios, in a clinic near you.',
        price: `£${this.priceLowest} - £${this.priceHighest}`,
        items: [
          {
            icon: 'place',
            text: 'Over 250 clinics nationwide'
          },
          {
            icon: 'medical',
            text: '30 minute consultation to create a personal treatment plan'
          }
        ]
      }
    },

    appointmentsTracking() {
      this.$gtm.trackEvent({
        event: 'searchresult',
        slots: this.getTotalSlots(),
        nearestdistance: this.getDistanceToNearestClinic(),
        nearesttimeslot: this.getHoursUntilFirstAppointment(),
      });
    },

    getTotalSlots() {
      const clinics = this.filteredAppointments;

      let i, total = 0;

      for (i in clinics) {
        if (clinics.hasOwnProperty(i)) {
          total += clinics[i].Appointments.length;
        }
      }

      return total;
    },

    getDistanceToNearestClinic() {
      const clinics = this.filteredAppointments;

      let i, distance;

      for (i in clinics) {
        if (clinics.hasOwnProperty(i)) {
          if (!distance || clinics[i].Distance < distance) {
            distance = clinics[i].Distance;
          }
        }
      }

      return distance;
    },

    getHoursUntilFirstAppointment() {
      const clinics = this.filteredAppointments, now = window.moment();

      let i, earliest1, earliest2, futureOnly = true;

      for (i in clinics) {
        if (clinics.hasOwnProperty(i)) {
          if (!clinics[i].Appointments.length) {
            continue;
          }

          if (!earliest1) {
            earliest1 = getEarliestAppointment(clinics[i], futureOnly);

            if (earliest1.isBefore(now)) {
              earliest1 = null;
            }

            continue;
          }

          earliest2 = getEarliestAppointment(clinics[i], futureOnly);

          if (earliest2.isBefore(earliest1) && earliest2.isSameOrAfter(now)) {
            earliest1 = earliest2;
          }
        }
      }

      if (!earliest1) {
        return null;
      }

      return parseInt(window.moment.duration(earliest1.diff(now)).asHours());
    },
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    });

    if (from.name && from.name === 'video-home') {
      location.reload();
    }
  },
}
</script>


<style lang="scss" scoped>
.appointments-header {
  @apply px-4 mx-auto w-full;
}

.price-card {
  background: white;
  border-radius: 10px;
}

.info-items {
  @media (min-width: 768px) {
    max-width: 48%;
  }
}

.cta {
  border-radius: 10px;

  h2 {
    font-size:  30px;
    line-height: 35px;
  }

  p {
    font-size: 20px;
    line-height: 25px;
  }

  a {
    max-width: 300px;
  }
}
</style>